import type { EpocusReview } from "$global-providers/rxdb-provider/collections/epocus-review";
import { Box, ButtonBase, styled, Typography } from "@material-ui/core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDicomPatientName } from "../../_lib/dicom-patient-name";
import { EXAM_LABELS } from "../../_lib/epocus-exam-constants";
import {
  EPOCUS_BORDER,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { usePerformerName } from "../../_lib/hooks/use-performer-name";
import {
  AGREEMENT_COLORS,
  AGREEMENT_LABELS,
  SELECTABLE_AGREEMENTS,
  type SelectableAgreement,
} from "../../_lib/review-agreement";
import { SectionTitle } from "../../_lib/stat-primitives";
import { linkToEpocusReviewPanel } from "../../links";
import {
  EmptyRow,
  ScrollTable,
  TABLE_CELL_STYLE,
} from "../../program/_lib/admin-primitives";
import type { ReviewHistoryRow } from "./use-reviewer-review-history";

const COLUMNS = [
  "Patient",
  "Exam type",
  "Performer",
  "Signed",
  "Turnaround",
  "Outcome",
] as const;

const EMPTY = "—";

const ClickableRow = styled("tr")({
  cursor: "pointer",
  "&:hover": { backgroundColor: EPOCUS_SELECTED_BG },
});

const FilterChip = styled(ButtonBase)({
  padding: "6px 12px",
  minHeight: 44,
  borderRadius: 20,
  fontSize: rem(12),
  fontWeight: 700,
  border: `1px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_WHITE,
  color: EPOCUS_TEXT_SECONDARY,
  "&.selected": {
    borderColor: "currentColor",
    backgroundColor: EPOCUS_SELECTED_BG,
  },
});

const OutcomeChip: FC<{ agreement: EpocusReview["agreement"] }> = ({
  agreement,
}) => (
  <Box
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 10px",
      borderRadius: 20,
      fontSize: rem(11),
      fontWeight: 700,
      whiteSpace: "nowrap",
      color: AGREEMENT_COLORS[agreement],
      border: `1px solid ${AGREEMENT_COLORS[agreement]}`,
    }}
  >
    {AGREEMENT_LABELS[agreement]}
  </Box>
);

const formatSignedAt = (isoDate: string): string => {
  const date = new Date(isoDate);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString([], {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : EMPTY;
};

const formatTurnaround = (minutes: number | null): string =>
  minutes === null ? EMPTY : `${Math.round(minutes)} min`;

export const outcomeBucket = (
  agreement: EpocusReview["agreement"],
): SelectableAgreement =>
  agreement === "disagree" ? "major_discrepancy" : agreement;

const HistoryRow: FC<{ row: ReviewHistoryRow }> = ({ row }) => {
  const navigate = useNavigate();
  // TODO(api-imaging-study#191): replace the per-row user query with the
  // batched `epocusPeople` lookup once that endpoint is on the schema.
  const { displayName } = usePerformerName(row.performerId);

  const patientName = formatDicomPatientName(row.patientName) || "Unnamed";

  return (
    <ClickableRow
      onClick={() => navigate(linkToEpocusReviewPanel(row.examId))}
      aria-label={`Open review for ${patientName}`}
    >
      <td
        style={{
          ...TABLE_CELL_STYLE,
          fontWeight: 600,
          color: EPOCUS_TEXT_PRIMARY,
        }}
      >
        {patientName}
      </td>
      <td style={TABLE_CELL_STYLE}>
        {row.examType ? (EXAM_LABELS[row.examType] ?? row.examType) : EMPTY}
      </td>
      <td style={{ ...TABLE_CELL_STYLE, color: EPOCUS_TEXT_SECONDARY }}>
        {displayName}
      </td>
      <td style={{ ...TABLE_CELL_STYLE, color: EPOCUS_TEXT_SECONDARY }}>
        {formatSignedAt(row.signedAt)}
      </td>
      <td style={TABLE_CELL_STYLE}>
        {formatTurnaround(row.turnaroundMinutes)}
      </td>
      <td style={TABLE_CELL_STYLE}>
        <OutcomeChip agreement={row.agreement} />
      </td>
    </ClickableRow>
  );
};

export const ReviewHistorySection: FC<{
  rows: ReviewHistoryRow[];
  windowLabel: string;
}> = ({ rows, windowLabel }) => {
  const [selected, setSelected] = useState<SelectableAgreement[]>([
    ...SELECTABLE_AGREEMENTS,
  ]);

  const toggle = (agreement: SelectableAgreement) =>
    setSelected((current) =>
      current.includes(agreement)
        ? current.filter((entry) => entry !== agreement)
        : [...current, agreement],
    );

  const visible = rows.filter((row) =>
    selected.includes(outcomeBucket(row.agreement)),
  );

  return (
    <Box>
      <SectionTitle>Review History</SectionTitle>

      <Box
        display="flex"
        flexWrap="wrap"
        style={{ gap: 8, marginBottom: 12 }}
        role="group"
        aria-label="Filter by outcome"
      >
        {SELECTABLE_AGREEMENTS.map((agreement) => {
          const isSelected = selected.includes(agreement);
          return (
            <FilterChip
              key={agreement}
              aria-pressed={isSelected}
              className={isSelected ? "selected" : undefined}
              onClick={() => toggle(agreement)}
              style={
                isSelected ? { color: AGREEMENT_COLORS[agreement] } : undefined
              }
            >
              {AGREEMENT_LABELS[agreement]}
            </FilterChip>
          );
        })}
      </Box>

      <ScrollTable columns={COLUMNS}>
        {visible.length === 0 ? (
          <EmptyRow colSpan={COLUMNS.length}>
            No signed reviews in {windowLabel}.
          </EmptyRow>
        ) : (
          visible.map((row) => <HistoryRow key={row.reviewId} row={row} />)
        )}
      </ScrollTable>

      <Typography
        style={{
          fontSize: rem(11),
          color: EPOCUS_TEXT_SECONDARY,
          marginTop: 8,
        }}
      >
        Reviews signed on another device appear once that exam is opened here.
      </Typography>
    </Box>
  );
};
